// Single source of truth for state metadata across the site.
// Add a state here to make it appear in the state selector and get its own URL structure.

export const STATES = {
  arizona: {
    code: 'AZ',
    name: 'Arizona',
    slug: 'arizona',
    regulator: 'ADRE',
    regulatorFull: 'Arizona Department of Real Estate',
    regulatorUrl: 'https://services.azre.gov/PdbWeb/School/ViewSchool/771',
    licenseLookupUrl: 'https://services.azre.gov/publicdatabase/SearchLicensee.aspx',
    schoolLicense: 'S25-0002',
    packagePrice: 49.99,
    coursePrice: 9.99,
    requiredHours: 24,
    cycleYears: 2,
    dailyHourCap: 9,
    lateFee: 125,
    onTimeFee: 60,
    live: true,        // approved school, students can enroll
    hasGuides: true,   // has SEO resource guides
  },
  kansas: {
    code: 'KS',
    name: 'Kansas',
    slug: 'kansas',
    regulator: 'KREC',
    regulatorFull: 'Kansas Real Estate Commission',
    regulatorUrl: 'https://www.krec.ks.gov/',
    licenseLookupUrl: 'https://licensing.ks.gov/verification/', // Kansas licensing portal
    schoolLicense: 'ED0190',
    packagePrice: 39.99,
    coursePrice: 14.99,
    requiredHours: 12,
    coreHours: 3,                        // 3 of the 12 hours must be the mandatory Required Core
    cycleYears: 2,
    reportsCompletions: true,            // Steady Education reports completions to KREC (students don't self-submit)
    reportingWindow: '3–5 calendar days',
    dailyHourCap: null, // no daily cap in KS
    creditTypes: ['core', 'elective'],   // course filter chips; omit to show the full (Arizona) list
    lateFee: null,
    onTimeFee: null,
    live: true,        // LIVE 2026-09-25 — all four courses KREC-approved and published
    reportingLimit: 'five calendar days', // KREC's legal filing limit (K.A.R.); reportingWindow is our promise
    pillarGuide: '/kansas-real-estate-ce-requirements',
    hasGuides: true,   // /kansas/resources hub is live (content in pages/resources/kansasHubContent.js)
  },
  // Expansion states — approved/planned next. Dropdown shows "coming soon" until courses launch.
  // NOTE: New Jersey's key MUST be 'new-jersey' to match its slug (getStateBySlug looks up STATES[slug]).
  utah:         { code: 'UT', name: 'Utah',       slug: 'utah',       regulator: 'UDRE',  requiredHours: 18, cycleYears: 2, live: false, hasGuides: false, comingSoon: true },
  'new-jersey': { code: 'NJ', name: 'New Jersey', slug: 'new-jersey', regulator: 'NJREC', requiredHours: 12, cycleYears: 2, live: false, hasGuides: false, comingSoon: true },
};

// Every state slug we route on (for prerender lists, sitemaps, etc.)
export const STATE_SLUGS = Object.keys(STATES);

// Look up a state by URL slug (case-insensitive)
export function getStateBySlug(slug) {
  if (!slug) return null;
  return STATES[slug.toLowerCase()] || null;
}

// Look up a state by 2-letter code (case-insensitive)
export function getStateByCode(code) {
  if (!code) return null;
  return Object.values(STATES).find(s => s.code === code.toUpperCase()) || null;
}

// The default state when we can't infer one from URL/localStorage
export const DEFAULT_STATE = STATES.arizona;

// Save/read user's preferred state (persisted across sessions)
export function getPreferredState() {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const stored = localStorage.getItem('se_state');
    if (stored) {
      // Legacy: stored as 'AZ' — look up by code
      const byCode = getStateByCode(stored);
      if (byCode) return byCode;
      // Also allow slug
      const bySlug = getStateBySlug(stored);
      if (bySlug) return bySlug;
    }
  } catch {}
  return DEFAULT_STATE;
}

export function setPreferredState(state) {
  if (typeof window === 'undefined' || !state) return;
  try {
    localStorage.setItem('se_state', state.code);
    // Broadcast so other components/tabs update
    window.dispatchEvent(new CustomEvent('stateChange', { detail: state.code }));
  } catch {}
}
